import { atom, selector, selectorFamily } from 'recoil'
import { customInsightsClient } from 'src/clients'
import { authData } from 'src/components/auth/util'
import { InsightImageWidget } from 'src/components/insight-image/definition'
import { InsightMapWidget } from 'src/components/insight-map/definition'
import { InsightTextWidget } from 'src/components/insight-text/definition'
import { Colors } from 'src/components/theme'
import { InsightBuilderWidget, InsightIconKey, CustomInsight } from 'src/types/insight-builder'
import { keyFactory } from 'src/utils/recoil'
import { INSIGHT_ICON_MAPPING } from 'src/views/insight-builder/insight-icon-selector'
import { ALL_WIDGETS } from 'src/views/insight-builder/widget-registry'

const key = keyFactory('insight-builder')

export const defaultInsightTitle = selector<string>({
  key: key('default-title'),
  get: ({ get }) => {
    const _customInsights = get(customInsights)
    const untitledInsights = _customInsights.filter((insight) =>
      (insight.title ?? '').toLowerCase().includes('untitled report')
    )

    if (untitledInsights.length > 0) {
      return `Untitled Report (${untitledInsights.length + 1})`
    }
    return 'Untitled Report'
  },
})

export const insightTitle = atom<string>({
  key: key('title'),
  default: defaultInsightTitle,
})

const randomIcon = selector<InsightIconKey>({
  key: key('random-icon'),
  get: () => {
    const icons = Object.keys(INSIGHT_ICON_MAPPING) as InsightIconKey[]
    return icons[Math.floor(Math.random() * icons.length)]
  },
})
export const insightIcon = atom<InsightIconKey>({
  key: key('icon'),
  default: randomIcon,
})

export const selectedWidgetsData = atom<CustomInsight['widgetsData']>({
  key: key('widgets-data'),
  default: [],
})

type SelectedWidgetsWithData = InsightBuilderWidget & { data: CustomInsight['widgetsData'][number] }
export const selectedWidgets = selector<SelectedWidgetsWithData[]>({
  key: key('selected-widgets'),
  get: ({ get }) => {
    const widgetsData = get(selectedWidgetsData)
    // find and return widgets in order of selection + filter out undefined
    return widgetsData
      .map((widgetData) => {
        // Misc Widgets that are found based on type
        // Their ids are a uuids since we can have multiple of the same widget
        if (widgetData.type === 'Text') {
          return {
            ...InsightTextWidget,
            data: widgetData,
            id: widgetData.id,
          }
        }
        if (widgetData.type === 'Image') {
          return {
            ...InsightImageWidget,
            data: widgetData,
            id: widgetData.id,
          }
        }
        if (widgetData.type === 'Map') {
          return {
            ...InsightMapWidget,
            data: widgetData,
            id: widgetData.id,
          }
        }

        // Alphamap Widgets that are found based on id
        const found = ALL_WIDGETS.find((widget) => widget.id === widgetData.id)
        if (!found) {
          return undefined
        }
        return {
          ...found,
          data: widgetData,
        }
      })
      .filter((widget) => !!widget) as SelectedWidgetsWithData[]
  },
})

export const isConfiguring = atom<boolean>({
  key: key('is-configuring'),
  default: false,
})

export const lastSaveDate = atom<Date>({
  key: key('last-save-date'),
  default: new Date(),
})

export const customInsights = selector<CustomInsight[]>({
  key: key('user-insights'),
  get: async ({ get }) => {
    const _authData = get(authData)
    if (!_authData) {
      return []
    }
    // refresh on save
    get(lastSaveDate)
    return await customInsightsClient.getCustomInsights()
  },
})

export const userCustomInsights = selectorFamily<CustomInsight[], string>({
  key: key('user-custom-insights'),
  get: (userId) => async () => {
    return await customInsightsClient.getCustomInsightsByUserId(userId)
  },
})

export const selectedCustomInsightId = atom<CustomInsight['id'] | undefined>({
  key: key('selected-user-insight-id'),
  default: undefined,
})

export const selectedCustomInsight = selector<CustomInsight | undefined>({
  key: key('selected-user-insight'),
  get: ({ get }) => {
    const _customInsights = get(customInsights)
    const id = get(selectedCustomInsightId)
    return _customInsights.find((insight) => insight.id === id)
  },
})

export const selectedCustomInsightTitle = selector<string | undefined>({
  key: key('selected-user-insight-title'),
  get: ({ get }) => {
    const insight = get(selectedCustomInsight)
    return insight?.title
  },
})

export const INSIGHT_COLOR = Colors.redMedium
